
import { useEffect, useState } from 'react';

interface TelegramWebApp {
  ready: () => void;
  expand: () => void;
  close: () => void;
  setHeaderColor: (color: string) => void;
  initData: string;
  initDataUnsafe: any;
  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
  };
  BackButton: {
    isVisible: boolean;
    show: () => void;
    hide: () => void;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
  };
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
  themeParams: {
    bg_color?: string;
    text_color?: string;
    hint_color?: string;
    link_color?: string;
    button_color?: string;
    button_text_color?: string;
  };
  openLink: (url: string) => void;
  openTelegramLink: (url: string) => void;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp;
    };
  }
}

export const useTelegramWebApp = () => {
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 10;
    
    const checkTelegramWebApp = () => {
      attempts++;
      console.log(`Checking for Telegram WebApp (attempt ${attempts})`);
      
      // Check if we're in Telegram environment
      if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
        console.log('Telegram WebApp found!');
        setWebApp(window.Telegram.WebApp);
        setIsReady(true);
        return;
      }
      
      // Additional checks for Telegram environment
      const isTelegramUA = navigator.userAgent.includes('Telegram');
      const hasTelegramScript = document.querySelector('script[src*="telegram-web-app"]');
      const hasTelegramData = window.location.search.includes('tgWebAppData');
      
      console.log('Telegram detection:', {
        userAgent: isTelegramUA,
        script: !!hasTelegramScript,
        data: hasTelegramData,
        windowTelegram: !!window.Telegram
      });
      
      if (attempts < maxAttempts && (isTelegramUA || hasTelegramScript || hasTelegramData)) {
        // Retry after a short delay
        setTimeout(checkTelegramWebApp, 100);
        return;
      }
      
      // For development/testing outside Telegram
      if (attempts >= maxAttempts) {
        console.warn('Telegram WebApp not available after maximum attempts - running in fallback mode');
      }
      setIsReady(true);
    };

    checkTelegramWebApp();
  }, []);

  return {
    webApp,
    isReady,
    isTelegram: !!webApp,
  };
};
